import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import DialogAppBar from "../../../Components/DialogAppBar";
import StockItemSearch from "../../../Components/StockItems/StockItemSearch";
import { getToken, requestErrorHandler } from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { showPopupSnackbar } from "../../../redux/actions/snackbarActions";
import FeatureSelectorDialog from "./FeatureSelectorDialog";

const server = EnvSettings.server;

const createFeatureRow = (overrides = {}) => ({
  key: overrides.key || `${Date.now()}-${Math.random()}`,
  id: overrides.id ?? null,
  name: overrides.name ?? "",
  description: overrides.description ?? "",
  selectedFeature: overrides.selectedFeature ?? null,
  displayFeature: overrides.displayFeature ?? null,
});

const defaultForm = {
  name: "",
  description: "",
  stockItem: null,
};

const normalizeFeaturesPayload = (features) =>
  features
    .map((feature) => {
      const trimmedName =
        typeof feature.name === "string" ? feature.name.trim() : "";
      if (!trimmedName) {
        return null;
      }
      return {
        id: feature.id || undefined,
        name: trimmedName,
        description:
          typeof feature.description === "string"
            ? feature.description.trim() || undefined
            : undefined,
      };
    })
    .filter(Boolean);

export default function AddFeatureList({
  open,
  onClose,
  onCompleted,
  initialFeatureList = null,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form, setForm] = useState(defaultForm);
  const [features, setFeatures] = useState([createFeatureRow()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featureOptions, setFeatureOptions] = useState([]);
  const [isFetchingFeatures, setIsFetchingFeatures] = useState(false);
  const [isFeatureDialogOpen, setIsFeatureDialogOpen] = useState(false);
  const isEditing = Boolean(initialFeatureList?.id);

  const dialogTitle = isEditing
    ? t("manufacturing.editFeatureList", "Edit feature list")
    : t("manufacturing.addFeatureList", "Add feature list");

  const resetForm = () => {
    setForm(defaultForm);
    setFeatures([createFeatureRow()]);
    setIsSubmitting(false);
  };

  const populateFromFeatureList = (featureList) => {
    if (!featureList) {
      resetForm();
      return;
    }
    setForm({
      name: featureList.name || "",
      description: featureList.description || "",
      stockItem: featureList.StockItem || null,
    });
    const populatedFeatures = featureList.features?.map((feature, index) =>
      createFeatureRow({
        id: feature.id ?? null,
        name: feature.name || "",
        description: feature.description || "",
        selectedFeature: feature.id
          ? {
              id: feature.id,
              name: feature.name || "",
              description: feature.description || "",
            }
          : null,
        displayFeature: feature.id
          ? {
              name: feature.name || "",
              description: feature.description || "",
            }
          : null,
        key: `existing-${feature.id || index}`,
      })
    ) || [createFeatureRow()];
    setFeatures(populatedFeatures);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }
    if (initialFeatureList) {
      populateFromFeatureList(initialFeatureList);
    } else {
      resetForm();
    }
  }, [initialFeatureList, open]);

  const normalizedFeatures = useMemo(
    () => normalizeFeaturesPayload(features),
    [features]
  );

  const addFeatureRow = (featureData) => {
    setFeatures([...features, createFeatureRow(featureData)]);
  };

  const removeFeatureRow = (key) => {
    if (features.length === 1) {
      return;
    }
    setFeatures(features.filter((feature) => feature.key !== key));
  };

  const fetchExistingFeatures = useCallback(() => {
    setIsFetchingFeatures(true);
    fetch(server + "/get_manufacturing_features", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setFeatureOptions([]);
          return;
        }
        const options = Array.isArray(result.manufacturingFeatures)
          ? result.manufacturingFeatures.map((item) => ({
              id: item?.id ?? null,
              name: item?.name || "",
              description: item?.description || "",
            }))
          : [];
        setFeatureOptions(options);
      })
      .catch(() => {
        setFeatureOptions([]);
      })
      .finally(() => setIsFetchingFeatures(false));
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchExistingFeatures();
  }, [open, fetchExistingFeatures]);

  const handleSubmit = () => {
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.featureListNameRequired",
            "Feature list name is required."
          ),
          severity: "warning",
        })
      );
      return;
    }
    if (!normalizedFeatures.length) {
      dispatch(
        showPopupSnackbar({
          message: t(
            "manufacturing.featureListFeaturesRequired",
            "Add at least one feature to the list."
          ),
          severity: "warning",
        })
      );
      return;
    }

    setIsSubmitting(true);
    const payload = {
      token: getToken(),
      name: trimmedName,
      description: form.description?.trim() || undefined,
      stockItemId: form.stockItem?.id || undefined,
      features: normalizedFeatures,
    };
    if (isEditing && initialFeatureList?.id) {
      payload.manufacturingFeatureListNameId = initialFeatureList.id;
    }

    fetch(
      server +
        (isEditing
          ? "/edit_manufacturing_feature_list"
          : "/add_manufacturing_feature_list"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          dispatch(
            showPopupSnackbar({
              message:
                result.message ||
                t(
                  "manufacturing.featureListSaveFailed",
                  isEditing
                    ? "Failed to update feature list."
                    : "Failed to create feature list."
                ),
              severity: "error",
            })
          );
          return;
        }
        dispatch(
          showPopupSnackbar({
            message: isEditing
              ? t(
                  "manufacturing.featureListUpdated",
                  "Feature list updated successfully."
                )
              : t(
                  "manufacturing.featureListCreated",
                  "Feature list created successfully."
                ),
            severity: "success",
          })
        );
        if (typeof onCompleted === "function") {
          onCompleted();
        } else if (typeof onClose === "function") {
          onClose();
        }
        resetForm();
      })
      .catch(() => {
        dispatch(
          showPopupSnackbar({
            message: t(
              "manufacturing.featureListSaveFailed",
              isEditing
                ? "Failed to update feature list."
                : "Failed to create feature list."
            ),
            severity: "error",
          })
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogAppBar header={dialogTitle} onClose={onClose} />
      <DialogContent style={{ paddingBottom: 120 }}>
        <Box mt={2} style={{ marginTop: 100 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("manufacturing.featureListName", "Feature list name")}
                fullWidth
                variant="outlined"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t(
                  "manufacturing.featureListDescription",
                  "Feature list description"
                )}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              {form.stockItem ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  border={1}
                  borderRadius={8}
                  borderColor="grey.300"
                  p={2}
                >
                  <Box>
                    <Typography variant="subtitle2">
                      {form.stockItem.sku}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {form.stockItem.name}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setForm({ ...form, stockItem: null })}
                  >
                    {t("common.change", "Change")}
                  </Button>
                </Box>
              ) : (
                <StockItemSearch
                  allowNewItems={false}
                  clearOnSelect
                  disableGridWrapper
                  label={t(
                    "manufacturing.featureListStockItem",
                    "Linked stock item"
                  )}
                  placeholder={t(
                    "manufacturing.searchStockItems",
                    "Search stock items"
                  )}
                  onChange={(_, value) => {
                    if (value && value.id) {
                      setForm({ ...form, stockItem: value });
                      return;
                    }
                    setForm({ ...form, stockItem: null });
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Box mt={4}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1">
                {t("manufacturing.features", "Features")}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                color="primary"
                variant="contained"
                onClick={() => setIsFeatureDialogOpen(true)}
              >
                {t("manufacturing.addFeature", "Add feature")}
              </Button>
            </Box>
            <FeatureSelectorDialog
              open={isFeatureDialogOpen}
              onClose={() => setIsFeatureDialogOpen(false)}
              onSubmit={({ mode, feature }) => {
                if (mode === "existing") {
                  if (
                    features.some(
                      (row) =>
                        row.id === feature.id ||
                        row.selectedFeature?.id === feature.id
                    )
                  ) {
                    dispatch(
                      showPopupSnackbar({
                        message: t(
                          "manufacturing.duplicateFeatureNotAllowed",
                          "This feature is already added to the list."
                        ),
                        severity: "warning",
                      })
                    );
                    return;
                  }
                  addFeatureRow({
                    id: feature.id ?? null,
                    name: feature.name || "",
                    description: feature.description || "",
                    selectedFeature: feature,
                    displayFeature: feature,
                  });
                } else {
                  addFeatureRow({
                    name: feature.name || "",
                    description: feature.description || "",
                  });
                }
                setIsFeatureDialogOpen(false);
              }}
              options={featureOptions}
              isLoading={isFetchingFeatures}
            />
            <List dense>
              {features.map((feature) => (
                <ListItem
                  key={feature.key}
                  divider
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                >
                  <Box width="100%">
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Box>
                        <Typography variant="subtitle2">
                          {feature.name}
                        </Typography>
                        {feature.description ? (
                          <Typography variant="body2" color="textSecondary">
                            {feature.description}
                          </Typography>
                        ) : null}
                      </Box>
                      <IconButton
                        onClick={() => removeFeatureRow(feature.key)}
                        disabled={features.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box height={100} />
        </Box>
      </DialogContent>
      <Box
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          padding: 16,
          backgroundColor: "white",
          boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.1)",
          zIndex: 10,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t("common.saving", "Saving…") : dialogTitle}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}
